// The sync logic over a Store (sync.md §4–§7): a local change applied and queued in one transaction,
// the outbox read in batches, a pulled page merged with pending changes laid on top, and a resync.
import { apply, emptyEntity, fold, rebase } from "./merge";
import { observe, tick, type HlcState } from "./hlc";
import { newNodeId, uuidv7 } from "./ids";
import type { Store, Tx } from "./store";
import type { Collection, Fields, LocalEntity, Mutation, OutboxRow, PullPage, SyncMeta } from "./types";

export const PUSH_LIMIT = 500;
/** Keep a push well under the server's 4 MB. */
export const PUSH_BYTES = 3 * 1024 * 1024;

async function metaOf(tx: Tx): Promise<SyncMeta> {
  return (await tx.getMeta()) ?? { node: newNodeId(), lastMs: 0, counter: 0, cursor: 0, resyncing: false, reflectionsMigrated: false };
}

export const readMeta = (store: Store) => store.tx("readonly", metaOf);

export async function patchMeta(store: Store, patch: Partial<SyncMeta>) {
  await store.tx("readwrite", async (tx) => tx.putMeta({ ...(await metaOf(tx)), ...patch }));
}

export type Change = { collection: Collection; id: string; op: "upsert" | "delete"; fields?: Fields };

/**
 * Apply local changes and queue them, in one transaction. Each is stamped from the stored clock
 * (persisted in the same transaction), applied to the local entity at once, and folded into a
 * queued, unsent upsert of the same entity where it can be.
 */
export async function mutate(store: Store, changes: Change[], now = Date.now()): Promise<LocalEntity[]> {
  return store.tx("readwrite", async (tx) => {
    const meta = await metaOf(tx);
    let clock: HlcState = { lastMs: meta.lastMs, counter: meta.counter, node: meta.node };
    const out: LocalEntity[] = [];
    for (const c of changes) {
      const t = tick(clock, now);
      clock = t.state;
      const m: Mutation = { mutationId: uuidv7(now), collection: c.collection, id: c.id, op: c.op, hlc: t.hlc, ...(c.op === "upsert" ? { fields: c.fields ?? {} } : {}) };
      const entity = apply((await tx.getEntity(c.collection, c.id)) ?? emptyEntity(c.collection, c.id), m, now);
      tx.putEntity(entity);
      out.push(entity);

      const queued = await tx.outboxFor(c.collection, c.id);
      const last = queued.at(-1);
      if (m.op === "delete") {
        // A queued delete makes earlier unsent upserts of the entity unnecessary.
        for (const r of queued) if (!r.inFlight && r.op === "upsert") tx.deleteOutbox(r.seq);
        tx.putOutbox({ ...m, attempts: 0, inFlight: false });
      } else if (last && !last.inFlight) {
        const merged = fold(last, m);
        if (merged) tx.putOutbox({ ...last, ...merged, mutationId: last.mutationId });
        else tx.putOutbox({ ...m, attempts: 0, inFlight: false });
      } else tx.putOutbox({ ...m, attempts: 0, inFlight: false });
    }
    tx.putMeta({ ...meta, lastMs: clock.lastMs, counter: clock.counter });
    return out;
  });
}

/** The next batch to push: in insertion order, at most 500 and about 3 MB, marked in flight. */
export async function takeBatch(store: Store, limit = PUSH_LIMIT, maxBytes = PUSH_BYTES): Promise<OutboxRow[]> {
  return store.tx("readwrite", async (tx) => {
    const rows = (await tx.outbox()).filter((r) => !r.inFlight);
    const batch: OutboxRow[] = [];
    let bytes = 0;
    for (const r of rows) {
      const size = JSON.stringify(toWire(r)).length;
      if (batch.length >= limit || (batch.length > 0 && bytes + size > maxBytes)) break;
      batch.push(r);
      bytes += size;
    }
    for (const r of batch) tx.putOutbox({ ...r, inFlight: true, attempts: r.attempts + 1 });
    return batch;
  });
}

/** The mutation as the server wants it. */
export const toWire = (r: Mutation): Mutation => ({
  mutationId: r.mutationId,
  collection: r.collection,
  id: r.id,
  op: r.op,
  ...(r.op === "upsert" ? { fields: r.fields ?? {} } : {}),
  hlc: r.hlc,
});

/** The server has these (applied or ignored): drop them. */
export async function settle(store: Store, rows: OutboxRow[]) {
  await store.tx("readwrite", async (tx) => rows.forEach((r) => tx.deleteOutbox(r.seq)));
}

/** A push that failed and will be retried: the rows go back in the queue. */
export async function release(store: Store, rows: OutboxRow[]) {
  await store.tx("readwrite", async (tx) => {
    const now = new Map((await tx.outbox()).map((r) => [r.seq, r]));
    for (const r of rows) {
      const cur = now.get(r.seq);
      if (cur) tx.putOutbox({ ...cur, inFlight: false });
    }
  });
}

/** Malformed mutations (400) go aside so the rest can sync. */
export async function deadLetter(store: Store, rows: OutboxRow[], reason: string, at = Date.now()) {
  await store.tx("readwrite", async (tx) => {
    for (const r of rows) {
      tx.deleteOutbox(r.seq);
      tx.putDead({ ...toWire(r), reason, at });
    }
  });
}

/** After a crash or a closed tab, nothing is really in flight. */
export async function resetInFlight(store: Store) {
  await store.tx("readwrite", async (tx) => {
    for (const r of await tx.outbox()) if (r.inFlight) tx.putOutbox({ ...r, inFlight: false });
  });
}

export const pendingCount = (store: Store) => store.tx("readonly", async (tx) => (await tx.outbox()).length);

/**
 * Merge a pulled page and move the cursor, in one transaction (sync.md §5). The server's entity
 * replaces the local one, then queued local mutations for it are laid on top; a tombstone removes
 * the local entity unless a queued upsert is newer than its delete. Returns the collections touched.
 */
export async function applyPage(store: Store, page: PullPage, seen?: Set<string>, now = Date.now()): Promise<Set<Collection>> {
  return store.tx("readwrite", async (tx) => {
    const meta = await metaOf(tx);
    let clock: HlcState = { lastMs: meta.lastMs, counter: meta.counter, node: meta.node };
    const touched = new Set<Collection>();
    for (const e of page.entities) {
      for (const h of [...Object.values(e.hlcs), e.liveHlc, e.deleteHlc]) if (h) clock = observe(clock, h, now);
      seen?.add(`${e.collection}/${e.id}`);
      const queued = await tx.outboxFor(e.collection, e.id);
      const merged = rebase(e, queued);
      if (merged.deleted && queued.length === 0) tx.deleteEntity(e.collection, e.id);
      else tx.putEntity(merged);
      touched.add(e.collection);
    }
    tx.putMeta({ ...meta, lastMs: clock.lastMs, counter: clock.counter, cursor: page.nextCursor });
    return touched;
  });
}

/** After a resync snapshot: drop every local entity the snapshot didn't hold and nothing local is waiting to send. */
export async function finishResync(store: Store, seen: Set<string>) {
  await store.tx("readwrite", async (tx) => {
    const queued = new Set((await tx.outbox()).map((r) => `${r.collection}/${r.id}`));
    for (const e of await tx.entities()) {
      const key = `${e.collection}/${e.id}`;
      if (!seen.has(key) && !queued.has(key)) tx.deleteEntity(e.collection, e.id);
    }
    tx.putMeta({ ...(await metaOf(tx)), resyncing: false });
  });
}

/** Live entities of a collection, newest first. */
export const liveEntities = (store: Store, collection: Collection) =>
  store.tx("readonly", async (tx) => (await tx.entities(collection)).filter((e) => !e.deleted).sort((a, b) => b.touched - a.touched));
